// src/pages/SchedulePage.jsx
// src/pages/SchedulePage.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast'; // Using react-hot-toast now
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { format, isBefore, parseISO, startOfMonth, endOfMonth, endOfDay, formatISO } from 'date-fns'; // Import date-fns helpers
import { localToUtc, utcStringToDate, formatDateToUtcString } from '../utilities/utilities';
import { api, BASE_URL } from '../utilities/api';
import { parseCents, formatCents } from '../utilities/cents'; // Assuming api utility is correctly set up
import { useAppointment } from '../contexts/AppointmentContext';
import { useUser } from '../contexts/UserContext';
import Spinner from '../components/Spinner';
import DoctorCard from '../components/DoctorCard';
import IntakeForm from '../components/IntakeForm';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import TimeSlotPicker from '../components/TimeSlotPicker';
import GenericConfirmationModal from '../components/GenericConfirmationModal';
import PaymentForm from '../components/PaymentForm';
import BookingConfirmationModal from '../components/BookingConfirmationModal';
import ErrorDisplay from '../components/ErrorDisplay';

// Helper function to format doctor name
const formatDoctorName = (doctor) => {
  return `${doctor.prefix || ''} ${doctor.first_name} ${doctor.last_name} ${doctor.suffix || ''}`.replace(/\s+/g, ' ').trim();
};

function SchedulePage() {
  const navigate = useNavigate();

  // State variables
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null); // Stores the mapped doctor object
  const [availableDays, setAvailableDays] = useState([]); // Stores array of 'YYYY-MM-DD' UTC date strings
  const [selectedDate, setSelectedDate] = useState(null); // Stores selected 'YYYY-MM-DD' UTC date string
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableTimes, setAvailableTimes] = useState([]); // Stores array of full ISO UTC timestamp strings
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null); // Stores the selected full ISO UTC timestamp string
  const [requirePayment, setRequirePayment] = useState(false); // tracks whether copay collection is required
  const [dialogMessage, setDialogMessage] = useState('');
  const [receiptData, setReceiptData] = useState({ pdfUrl: null, pdfFile: null });
  const [showPaymentReceipt, setShowPaymentReceipt] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [bookedAppointmentSummary, setBookedAppointmentSummary] = useState(null);
  
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showPayConfirmation, setShowPayConfirmation] = useState(false);
  const [payConfirmationShown, setPayConfirmationShown] = useState(false);
  const [viewingMoreTimes, setViewingMoreTimes] = useState(false);
  const [showIntake, setShowIntake] = useState(false);
  const [showTimes, setShowTimes] = useState(false);

  const [sendMessage, setSendMessage] = useState(false);
  const [intakeString, setIntakeString] = useState(null);

  // Loading states
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(true);
  const [isLoadingDays, setIsLoadingDays] = useState(false);
  const [isLoadingTimes, setIsLoadingTimes] = useState(false);
  const [isBooking, setIsBooking] = useState(false);

  // Error states
  const [errorDoctors, setErrorDoctors] = useState(null);
  const [errorAvailability, setErrorAvailability] = useState(null);
  const [errorBooking, setErrorBooking] = useState(null);
  const availableDaysRequestId = useRef(0);
  const availableTimesRequestId = useRef(0);
  const bookingSubmissionInFlight = useRef(false);

  const { user, loadingSession } = useUser();
  const { refreshAppointments, bookAppointment, reserveAppointmentPending, cancelPendingAppointment } = useAppointment();

  // Pending appointment state — set when reserve succeeds, cleared on capture or cancel
  const [pendingAppointmentId, setPendingAppointmentId] = useState(null);
  const [, setPendingExpiresAt] = useState(null);
  // The server's provider choice and copay (cents) from the reserve response.
  // The browser computes no money and holds no provider decision.
  const [pendingProvider, setPendingProvider] = useState(null);
  const [pendingTotalCents, setPendingTotalCents] = useState(null);
  // Flag set when the capture succeeds so the unmount cleanup knows not to cancel
  const pendingFinalizedRef = useRef(false);
  const firstName = user?.first_name || user?.firstname || '';

  // Eagerly cancel a pending reservation on any user-action that backs out of
  // the payment flow before finalization. Idempotent — safe to call multiple
  // times (e.g. if the unmount cleanup also fires). Clears state immediately
  // so the UI is consistent regardless of the async result.
  const cancelPendingIfNeeded = useCallback((apptId) => {
    if (!apptId || pendingFinalizedRef.current) return;
    setPendingAppointmentId(null);
    setPendingExpiresAt(null);
    setPendingProvider(null);
    setPendingTotalCents(null);
    cancelPendingAppointment(apptId).catch(() => {
      // Best-effort: the server's 15-min TTL will reclaim the slot if this fails
    });
  }, [cancelPendingAppointment]);

  const selectedAppointmentDateTime = selectedTimeSlot
    ? (selectedTimeSlot instanceof Date ? selectedTimeSlot : parseISO(selectedTimeSlot))
    : null;
  const selectedAppointmentDateLabel = selectedAppointmentDateTime ? format(selectedAppointmentDateTime, 'PPPP') : 'Not selected';
  const selectedAppointmentTimeLabel = selectedAppointmentDateTime ? format(selectedAppointmentDateTime, 'p') : 'Not selected';
  const copayLabel = requirePayment ? `$${Number.parseFloat(user?.copay_fee || 0).toFixed(2)}` : '$0.00';

  // --- Pending appointment cleanup ---
  // On unmount: if there is an unreleased pending slot (user navigated away
  // before completing payment), fire a keepalive cancel so the server doesn't
  // have to wait for the 15-min TTL to reclaim the slot.
  useEffect(() => {
    // Keep a stable ref to the current pendingAppointmentId so the cleanup
    // closure captures the latest value, not the stale closure value.
    pendingFinalizedRef.current = false; // reset on each render cycle is fine
  });

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pendingAppointmentId && !pendingFinalizedRef.current) {
        api.beaconCancelPendingAppointment(pendingAppointmentId);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Unmount cleanup — fires when user navigates away via React Router
      if (pendingAppointmentId && !pendingFinalizedRef.current) {
        api.beaconCancelPendingAppointment(pendingAppointmentId);
      }
    };
  }, [pendingAppointmentId]);

  // --- Data Fetching ---
  // Fetch available days for the selected DOCTOR for a given date range
  // The input dates represent the exact start/end moments (UTC timestamps)
  // corresponding to the local month boundaries.
  const fetchAvailableDays = useCallback((doctorId, rangeStartDateString, rangeEndDateString) => {
    const requestId = availableDaysRequestId.current + 1;
    availableDaysRequestId.current = requestId;
    setIsLoadingDays(true);
    setErrorAvailability(null);
    setAvailableDays([]);

    const startParam = formatDateToUtcString(new Date(parseISO(rangeStartDateString)));
    const endParam = formatDateToUtcString(new Date(parseISO(rangeEndDateString)));

    api.getAvailableDays({
      startdate: startParam,
      enddate: endParam,
      userid: doctorId,
    })
      .then(response => {
        if (requestId !== availableDaysRequestId.current) return;
        const uniqueDays = (response?.data || []).reduce((acc, item) => {
          if (!acc.seen.has(item.day)) {
            acc.seen.add(item.day);
            acc.results.push(item.start); // keep first ISO string for that day
          }
          return acc;
        }, {
          seen: new Set(), results: []
        }).results;
        //.sort(); // Optional, keeps dates in chronological order

        setAvailableDays(uniqueDays); // ✅ Now you have just one entry per date
      })
      .catch(() => {
        if (requestId !== availableDaysRequestId.current) return;
        setErrorAvailability('Failed to load available days for the selected doctor.');
        setAvailableDays([]);
      })
      .finally(() => {
        if (requestId === availableDaysRequestId.current) setIsLoadingDays(false);
      });
  }, [selectedDoctor, selectedDate, showTimes]);

  // Fetch available doctors
  const fetchDoctors = useCallback(() => {
    if (!user || !user.userid) {
      setErrorDoctors("User information is missing.");
      setIsLoadingDoctors(false);
      return;
    }

    setIsLoadingDoctors(true);
    setErrorDoctors(null);
    api.getAvailableDoctors({ user_id: user.userid })
      .then(response => {
        const rawDoctors = response?.data || [];
        const mappedDoctors = rawDoctors.map(doc => ({
          userid: doc.doctor_user_id || doc.user_id,
          name: formatDoctorName(doc),
          bio: doc.doctor_bio || 'No bio available.',
          nextAvail: utcStringToDate(doc.datetime) || '',
          avatar: doc.avatar_file || null,
          avatarUrl: doc.avatar_url || null,
          raw: doc,
        }));
        setDoctors(mappedDoctors);
      })
      .catch(() => {
        setErrorDoctors("Failed to load available doctors. Please try again later.");
        setDoctors([]);
      })
      .finally(() => {
        setIsLoadingDoctors(false);
      });
  }, [user]); // user as a dependency

  // --- useEffect to load initially
  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  useEffect(() => {
    if (!user || !user.userid) return;
    refreshAppointments();
  }, [refreshAppointments, user]);

  // Determine whether the appointment requires a copay charge
  useEffect(() => {
    if (!user?.userid) return;

    setPayConfirmationShown(false);

    const rawCopay = user?.copay_fee ?? 0;
    const copayValue = Number.parseFloat(rawCopay);
    const hasCopayCharge = Number.isFinite(copayValue) && copayValue > 0;

    setRequirePayment(hasCopayCharge);

    if (hasCopayCharge) {
      const formattedAmount = copayValue.toFixed(2);
      const planCode = typeof user?.regcode === 'string' ? user.regcode.trim() : '';
      const planLabel = planCode ? `${planCode.toUpperCase()} plan` : 'plan';
      const message = `Your ${planLabel} requires a co-pay of $${formattedAmount} for each appointment.`;

      setDialogMessage(message);
      setShowPayConfirmation(true);
    } else {
      setDialogMessage(null);
      setShowPayConfirmation(false);
    }
  }, [user]);

  // Fetch available times for the selected DOCTOR on the selected UTC DATE
  useEffect(() => {
    if (!selectedDoctor || !selectedDate) { // selectedDate is 'YYYY-MM-DD' UTC
      availableTimesRequestId.current += 1;
      setAvailableTimes([]);
      return;
    }
    const requestId = availableTimesRequestId.current + 1;
    availableTimesRequestId.current = requestId;

    // 1. Start of the day in UTC is already selectedDate
    const workingDate = parseISO(selectedDate);
    const nowUtc = new Date();  //current time

    // start of day can be no earlier than right now
    const startOfDayUtc = isBefore(workingDate, nowUtc)
      ? nowUtc
      : workingDate;

    // 2. Calculate the end of the day in UTC using endOfDay
    const endOfDayLocal = endOfDay(new Date(startOfDayUtc)); // This is 23:59:59.999 local time
    const endOfDayUtc = new Date(endOfDayLocal.toISOString()); // This is a UTC Date object

    setIsLoadingTimes(true);
    setErrorAvailability(null);
    setAvailableTimes([]);

    // API expects start/end for the specific day in UTC         
    api.getAvailableTimes({
      startdate: formatDateToUtcString(startOfDayUtc),
      enddate: formatDateToUtcString(endOfDayUtc),
      userid: selectedDoctor.userid
    })
      .then(response => {
        if (requestId !== availableTimesRequestId.current) return;
        // API returns { start: "YYYY-MM-DDTHH:mm:ss+00:00" } (ISO 8601 UTC)
        const times = (response?.data || []).map(timeSlot => ({
          start: timeSlot.start,
          title: timeSlot.title
        }));
        setAvailableTimes(times); // now availableTimes is an array of { start, title }
      })
      .catch(() => {
        if (requestId !== availableTimesRequestId.current) return;
        setErrorAvailability("Failed to load available times for the selected date.");
        setAvailableTimes([]);
      })
      .finally(() => {
        if (requestId === availableTimesRequestId.current) setIsLoadingTimes(false);
      });
  }, [selectedDoctor, selectedDate]); // Dependencies are correct


  // --- Event Handlers ---
  const handleRefresh = () => {
    fetchDoctors();
    toast.success('Availability refreshed!');
  };

  const handleMonthChange = useCallback((newMonth) => {
    if (!selectedDoctor) return;
    setSelectedDate(null);
    setSelectedTimeSlot(null);
    setCurrentMonth(newMonth);

    // 1. Get local start of month
    const localStart = startOfMonth(newMonth);  // e.g. 2025-04-01T00:00:00 LOCAL

    // 2. Get local end of month
    const localEnd = endOfMonth(newMonth);      // e.g. 2025-04-30T23:59:59.999 LOCAL

    // 3. Convert local to UTC
    const utcStart = new Date(localStart.getTime() + (localStart.getTimezoneOffset() * 60000));
    const utcEnd = new Date(localEnd.getTime() + (localEnd.getTimezoneOffset() * 60000));

    // 4. Format as strings if needed for API
    const startDateString = formatISO(utcStart).slice(0, 19) + 'Z';  // "2025-04-01T05:00:00.000Z"
    const endDateString = formatISO(utcEnd).slice(0, 19) + 'Z';      // "2025-05-01T04:59:59.999Z"

    // Fetch available days
    fetchAvailableDays(selectedDoctor.userid, startDateString, endDateString);
  }, [selectedDoctor, fetchAvailableDays]);

  const handleViewMoreTimes = (doctor) => {
    const now = new Date();
    setBookedAppointmentSummary(null);
    setSelectedDoctor(doctor);
    setCurrentMonth(now);
    setViewingMoreTimes(true);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
    setErrorAvailability(null);
    setErrorBooking(null);
    setAvailableDays([]);
    setAvailableTimes([]);
    setShowTimes(false);

    const localStartOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0); // 00:00:00.000 CDT
    const localEndOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999); // Last day, 23:59:59.999 CDT

    const utcStartOfMonth = localToUtc(localStartOfMonth).toISOString();
    const utcEndOfMonth = localToUtc(localEndOfMonth).toISOString();

    fetchAvailableDays(doctor.userid, utcStartOfMonth, utcEndOfMonth);
  };
  
  // Receives 'YYYY-MM-DD' string representing a UTC date from the calendar
  const handleDateSelect = (date) => {
    setShowTimes(true);
    setSelectedDate(date);
    setSelectedTimeSlot(null); // Reset time slot when date changes
  };

  // Receives the full ISO UTC timestamp string from the time picker
  const handleFirstAvailableTimeSelect = (doctor, isoTimestampString) => {
    setSelectedDoctor(doctor);
    handleTimeSelect(isoTimestampString);
  };

  // Receives the full ISO UTC timestamp string from the time picker
  const handleTimeSelect = (isoTimestampString) => {
    setBookedAppointmentSummary(null);
    setSelectedTimeSlot(isoTimestampString);
    setShowIntake(true);
    setIsBooking(false);
  };

  // copay was due and patient refused
  const handleRefuseCopay = () => {
    setShowPayConfirmation(false);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
    setShowIntake(false);
    setShowTimes(false);
    setIsBooking(false);
    navigate("/dashboard");
  }

  // copay was due and patient refused
  const handleConfirmCopay = () => {
    setShowPayConfirmation(false);
    setPayConfirmationShown(true);
  }

  const handleIntakeSubmit = (formData) => {
    const formString = Object.entries(formData)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');

    setIntakeString(formString);
    setShowTimes(false);
    setShowIntake(false);
    setShowConfirmation(true);
  }

  const handleConfirmBooking = () => {
    if (!selectedDoctor || !selectedTimeSlot || !user || !user.userid) {
      toast.error("Missing information to book appointment.");
      return;
    }

    setShowConfirmation(false);

    if (requirePayment) {
      // Reserve the slot BEFORE opening the payment form so we hold
      // the time while the server captures and books.
      setIsBooking(true);
      setErrorBooking(null);
      const reserveData = {
        apptdate: formatDateToUtcString(
          selectedTimeSlot instanceof Date
            ? selectedTimeSlot
            : parseISO(selectedTimeSlot)
        ),
        doctor_user_id: selectedDoctor.userid,
        intake: intakeString || '',
        origin: window.location.origin,
      };
      reserveAppointmentPending(reserveData)
        .then(response => {
          // 422 contact_required — route to profile before any money moves
          if (handleContactRequired(response)) return;
          if (!response.success) {
            throw new Error(response.error || 'Could not reserve appointment slot. Please try again.');
          }
          // Slot is held. The server's copay total is the only amount ever
          // shown or charged; with none, there is nothing to pay, so release
          // the hold instead of opening a pay control.
          if (parseCents(response.total_cents) === null) {
            cancelPendingIfNeeded(response.appointment_id);
            throw new Error('We cannot show the copay amount right now, so there is nothing to pay yet. You have not been charged. Please try again shortly, or call the office.');
          }
          setPendingAppointmentId(response.appointment_id);
          setPendingExpiresAt(response.expires_at);
          setPendingProvider(response.provider || null);
          setPendingTotalCents(response.total_cents);
          pendingFinalizedRef.current = false;
          setShowPaymentForm(true);
        })
        .catch(err => {
          const msg = err.message || 'Could not reserve appointment slot. Please try again.';
          setErrorBooking(msg);
          toast.error(msg);
        })
        .finally(() => {
          setIsBooking(false);
        });
    } else {
      // Book directly if no payment is required
      handleBookAppointment();
    }
  };

  // Server-gate failure handler — when remote_book / remote_book_with_payment
  // returns 422 contact_required, route the patient to Profile → Contact so
  // they can verify whichever channel failed. Returns true if the response
  // was a contact_required failure and we routed away, false otherwise.
  // PayPal capture happens BEFORE this in the with-payment path — that's why
  // the toast specifically warns the patient and tells them what to fix.
  const handleContactRequired = (response) => {
    if (!response || response.error !== 'contact_required') return false;
    const need = (!response.email_ok && !response.phone_ok)
      ? 'verify both your email and your phone'
      : (!response.email_ok)
        ? 'verify your email address'
        : 'verify your phone number';
    toast.error(
      `Your appointment can't be confirmed until you ${need}. ` +
      `Taking you to your Profile so you can update your contact info.`
    );
    navigate('/profile?tab=contact');
    return true;
  };

  // Payment error handler
  const handlePaymentError = (error) => {
    // Allow continuing on the page for cancellations
    if (error.type === 'cancelled' || error.cancelled) {
      // The PaymentForm will reset itself with a new key
      return;
    }

    // A payment failure keeps the form and the reservation: the payment
    // control shows the server's message and the patient can try again. Back
    // buttons and the pagehide beacon still release the slot.
  };

  // handle standard booking
  const handleBookAppointment = () => {
    if (isBooking || bookingSubmissionInFlight.current) return;
    if (!selectedDoctor?.userid || !selectedTimeSlot) {
      setErrorBooking('Choose a provider and appointment time before booking.');
      return;
    }

    bookingSubmissionInFlight.current = true;
    setIsBooking(true);
    setErrorBooking(null);
    
    const bookingData = {
      apptdate: formatDateToUtcString(
        selectedTimeSlot instanceof Date
          ? selectedTimeSlot
          : parseISO(selectedTimeSlot)
      ),
      doctor_user_id: selectedDoctor.userid,
      sendmsg: sendMessage,
      origin: window.location.origin,
      intake: intakeString || '',
    };

    bookAppointment(bookingData)
      .then(response => {
        // Server gate failure: route to Profile so the patient can verify
        // their email/phone. The 422 detail tells us which channel failed.
        if (handleContactRequired(response)) return;
        // Format time for display in toast using helper
        if (!response.success) {
          throw new Error(response.error);
        }
        const localTimeStr = format(
          selectedTimeSlot instanceof Date ? selectedTimeSlot : parseISO(selectedTimeSlot),
          "PP p"
        );
        setBookedAppointmentSummary({
          provider: selectedDoctor.name,
          dateTime: selectedTimeSlot,
          copay: copayLabel,
          receipt: null,
          paymentStatus: 'No payment due',
        });
        toast.success(`Appointment with ${selectedDoctor.name} booked successfully for ${localTimeStr}!`);
      })
      .catch(err => {
        const errorMsg = err.message || "Failed to book appointment. Please try again.";
        setErrorBooking(errorMsg);
        toast.error(errorMsg);
      })
      .finally(() => {
        bookingSubmissionInFlight.current = false;
        setIsBooking(false);
      });
  };
  
  // Payment success handler. The server has already captured the payment and
  // booked the appointment; `response` is its capture reply.
  const handlePaymentSuccess = (response) => {
    if (!response?.success) {
      setErrorBooking(response?.message || 'Payment failed. Please try again.');
      return;
    }

    // Booked: the unmount/pagehide cleanup must not cancel it.
    pendingFinalizedRef.current = true;
    setPendingAppointmentId(null);
    setPendingExpiresAt(null);
    setPendingProvider(null);
    setPendingTotalCents(null);
    setErrorBooking(null);

    const localTimeStr = format(
      selectedTimeSlot instanceof Date ? selectedTimeSlot : parseISO(selectedTimeSlot),
      "PP p"
    );
    toast.success(`Appointment with ${selectedDoctor.name} booked successfully for ${localTimeStr}!`);

    if (response.url) {
      setReceiptData({ pdfUrl: response.url });
      setShowPaymentReceipt(true);
      // Keep showing the payment form so the receipt can be displayed
      setShowPaymentForm(true);
    } else {
      setShowPaymentForm(false);
    }
    setBookedAppointmentSummary({
      provider: selectedDoctor.name,
      dateTime: selectedTimeSlot,
      copay: `$${formatCents(pendingTotalCents) ?? '0.00'}`,
      receipt: response.url ? { url: response.url } : null,
      paymentStatus: 'Paid',
    });
    refreshAppointments();
  };

  const handleCancelBooking = () => {
    setShowConfirmation(false);
  };

  const handleBackToDateSelection = () => {
    cancelPendingIfNeeded(pendingAppointmentId);
    setShowPaymentForm(false);
    setShowTimes(false);
    setShowIntake(false);
  }

  const handleBackToDoctorSelection = () => {
    cancelPendingIfNeeded(pendingAppointmentId);
    setShowPaymentForm(false);
    setBookedAppointmentSummary(null);
    setSelectedDoctor(null);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
    setViewingMoreTimes(false);
    setErrorAvailability(null);
    setErrorBooking(null);
    setAvailableDays([]);
    setAvailableTimes([]);
    setShowTimes(false);
    setShowIntake(false);
  };

  const renderAppointmentSummary = () => {
    if (!selectedDoctor && !selectedTimeSlot) return null;

    return (
      <section className="w-full border border-gray-200 bg-white rounded p-4 mb-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h3 className="font-semibold text-primary">Appointment Summary</h3>
            <p className="text-sm text-neutral-dark">Not booked yet. Review and confirm before leaving this flow.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm md:text-right">
            <p><span className="font-semibold text-gray-600">Provider:</span> {selectedDoctor?.name || 'Not selected'}</p>
            <p><span className="font-semibold text-gray-600">Date:</span> {selectedAppointmentDateLabel}</p>
            <p><span className="font-semibold text-gray-600">Time:</span> {selectedAppointmentTimeLabel}</p>
            <p><span className="font-semibold text-gray-600">Copay:</span> {copayLabel}</p>
          </div>
        </div>
      </section>
    );
  };

  const renderBookedConfirmation = () => {
    if (!bookedAppointmentSummary) return null;
    const date = bookedAppointmentSummary.dateTime instanceof Date
      ? bookedAppointmentSummary.dateTime
      : parseISO(bookedAppointmentSummary.dateTime);

    return (
      <section className="w-full border border-green-200 bg-white rounded p-6 text-center">
        <h2 className="text-2xl font-semibold text-primary mb-2">Appointment Booked</h2>
        <p className="text-neutral-dark mb-4">Your appointment is confirmed.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm text-left md:text-center mb-5">
          <p><span className="font-semibold text-gray-600">Provider:</span> {bookedAppointmentSummary.provider}</p>
          <p><span className="font-semibold text-gray-600">Date:</span> {format(date, 'PPPP')}</p>
          <p><span className="font-semibold text-gray-600">Time:</span> {format(date, 'p')}</p>
          <p><span className="font-semibold text-gray-600">Payment:</span> {bookedAppointmentSummary.paymentStatus}</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded p-4 text-sm text-neutral-dark text-left mb-5">
          Join instructions will appear on your dashboard before the appointment window opens. Cancel or reschedule from your appointment list if your plans change.
        </div>
        <div className="flex flex-col sm:flex-row justify-center gap-2">
          <button type="button" onClick={() => navigate('/appointments')} className="px-4 py-3 bg-secondary text-white rounded hover:bg-secondary-dark">
            View Appointments
          </button>
          {bookedAppointmentSummary.receipt?.url && (
            <a href={bookedAppointmentSummary.receipt.url} target="_blank" rel="noopener noreferrer" className="px-4 py-3 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
              View receipt
            </a>
          )}
        </div>
      </section>
    );
  };

  const renderBookingError = () => {
    if (!errorBooking) return null;

    return (
      <div className="my-4">
        <ErrorDisplay message={errorBooking} />
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <button type="button" onClick={handleBackToDateSelection} className="px-3 py-2 bg-button text-white rounded hover:bg-button-light">
            Choose another time
          </button>
          <button type="button" onClick={handleBackToDoctorSelection} className="px-3 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
            Back to providers
          </button>
        </div>
      </div>
    );
  };

  // --- Rendering Logic ---

  const renderDoctorSelection = () => (
    <div className="w-full">
      <h2 className="text-2xl font-semibold mb-1 text-center text-primary-dark">Select a Provider</h2>
      {/* Motivational/refresh note */}
      <div className="text-center text-sm text-neutral-dark mb-4">
        <p>
          Appointments are first-come, first-served! <br />
          Availability updates often. If you've been browsing a few minutes, you may want to refresh.
        </p>
        <button
          type="button"
          onClick={handleRefresh}
          className="mt-2 text-primary underline hover:text-primary-dark transition-colors duration-150 text-xs"
        >
          Refresh Availability
        </button>
      </div>


      {errorDoctors && <ErrorDisplay message={errorDoctors} />}
      {renderBookingError()}
	      {isLoadingDoctors && <Spinner text="Loading doctors..." />}
	      {!isLoadingDoctors && !errorDoctors && doctors.length === 0 && (
	        <div className="text-center border border-gray-200 bg-gray-50 rounded p-4">
	          <p className="text-gray-600 mb-3">No providers are currently available for your plan.</p>
	          <button
	            type="button"
	            onClick={handleRefresh}
	            className="px-3 py-2 bg-button text-white rounded hover:bg-button-light"
	          >
	            Refresh availability
	          </button>
	        </div>
	      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {doctors.map(doctor => (
          <DoctorCard
            key={doctor.userid}
            doctor={doctor} // Pass the mapped doctor object
            onViewMoreTimes={() => handleViewMoreTimes(doctor)} // Always use this action
            onSelectTime={() => handleFirstAvailableTimeSelect(doctor, doctor.nextAvail)}
            nextAvail={doctor.nextAvail}
          />
        ))}
      </div>
    </div>
  );

  const renderAvailabilitySelection = () => (
    <div className="w-full flex flex-col items-center">
      <button
        type="button"
        onClick={handleBackToDoctorSelection}
        className="mb-0 text-primary-light hover:underline self-start"
      >
        &larr; Back to providers
      </button>
      {renderAppointmentSummary()}
      {selectedDoctor && (<h2 className="text-xl mb-4 font-semibold text-primary self-center">{selectedDoctor.name}</h2>)}
      {/* Calendar */}
      {!showTimes && (
        <div className="w-full  mb-6">
          <h3 className="text-lg text-center font-medium mb-3 text-gray-800">Choose Date</h3>
          <AvailabilityCalendar
            doctorId={selectedDoctor.userid}
            onDateSelect={handleDateSelect}
            selectedDate={selectedDate} // Pass 'YYYY-MM-DD' UTC
            onMonthChange={handleMonthChange}
            highlightDays={availableDays} // Pass 'YYYY-MM-DD' UTC strings
            isLoading={isLoadingDays}
            currentMonth={currentMonth}
          />
        </div>
      )}

      {/* Time Slot Picker */}
      {(selectedDate && showTimes) && (
        <div className="w-full mb-6">
          {/* Display selected date formatted locally */}
          <h3 className="text-xl font-medium mb-3 text-gray-800">
            Choose an Appointment Time for {format(
              selectedDate instanceof Date ? selectedDate : parseISO(selectedDate),
              'PPPP'
            )}
          </h3>
          <button
            type="button"
            onClick={handleBackToDateSelection}
            className="mb-4 text-primary-light hover:underline self-start"
          >
            &larr; Choose Different Date
          </button>

          {isLoadingTimes && <Spinner text="Loading times..." />}
          {!isLoadingTimes && availableTimes.length === 0 && !errorAvailability && (
            <div className="text-center border border-gray-200 bg-gray-50 rounded p-4">
              <p className="text-gray-600 mb-3">No available times for this date.</p>
              <div className="flex flex-col sm:flex-row justify-center gap-2">
                <button type="button" onClick={handleBackToDateSelection} className="px-3 py-2 bg-button text-white rounded hover:bg-button-light">
                  Choose another date
                </button>
                <button type="button" onClick={handleBackToDoctorSelection} className="px-3 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
                  Back to providers
                </button>
              </div>
            </div>
          )}
          <TimeSlotPicker
            availableTimes={availableTimes} // Pass array of full ISO UTC strings
            onTimeSelect={handleTimeSelect} // Expects full ISO UTC string back
            selectedTime={selectedTimeSlot} // Pass the selected full ISO UTC string
          />
        </div>
      )}

      {errorAvailability && <ErrorDisplay message={errorAvailability} className="my-4" />}
      {renderBookingError()}
    </div>
  );

  const renderIntakeForm = () => (
    <div className="w-full p-2 md:p-0 flex flex-col flex-grow justify-center mb-0 pb-0">
      {renderAppointmentSummary()}
      {/* Display selected date formatted locally */}
      <button
        type="button"
        onClick={handleBackToDateSelection}
        className="mb-2 text-primary-light hover:underline self-start"
      >
        <FontAwesomeIcon icon={faArrowLeft} /> Choose Different Date
      </button>
      {renderBookingError()}
      <div>
        <h3 className="text-2xl text-center font-medium mb-0 text-primary">
          Welcome {firstName}
        </h3>
      </div>
      {/* Intake form */}
      <IntakeForm
        onSubmit={handleIntakeSubmit}
        onCancel={handleBackToDoctorSelection}
        isRefill={false}
      />
    </div>
  );

  const renderCollectCopay = () => {
    // Format dates as strings
    const formattedDate = selectedTimeSlot 
      ? (selectedTimeSlot instanceof Date 
        ? format(selectedTimeSlot, 'yyyy-MM-dd') 
        : format(parseISO(selectedTimeSlot), 'yyyy-MM-dd'))
      : '';
      
    const formattedTime = selectedTimeSlot
      ? (selectedTimeSlot instanceof Date
        ? format(selectedTimeSlot, 'HH:mm:ss')
        : format(parseISO(selectedTimeSlot), 'HH:mm:ss'))
      : '';

    return (
      <div className="w-full p-2 md:p-0 flex flex-col flex-grow justify-center mb-0 pb-0">
        {renderBookingError()}

	        <PaymentForm
          user={user}
          appointment={{
            copay_amount: formatCents(pendingTotalCents),
            provider: pendingProvider,
            date: formattedDate,
            time: formattedTime,
            doctor_user_id: selectedDoctor?.userid,
            provider_name: selectedDoctor?.name,
            service_name: 'Telemedicine Appointment',
            appointment_id: pendingAppointmentId,
          }}
          appointmentId={pendingAppointmentId}
          showReceipt={showPaymentReceipt}
          receiptData={receiptData}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentError}
	        />
	      </div>
	    );
	  }

	  return (
	    <>
      {(loadingSession || isBooking) && <Spinner overlay size="md" />}
	      {bookedAppointmentSummary && renderBookedConfirmation()}
	      {!bookedAppointmentSummary && (!selectedDoctor || (!viewingMoreTimes && !showIntake)) && !showPaymentForm && !showPaymentReceipt && renderDoctorSelection()}
	      {!bookedAppointmentSummary && (selectedDoctor && viewingMoreTimes && !showIntake) && !showPaymentForm && !showPaymentReceipt && renderAvailabilitySelection()}
	      {!bookedAppointmentSummary && (showIntake && selectedDoctor && selectedTimeSlot) && !showPaymentForm && !showPaymentReceipt && renderIntakeForm()}
	      {!bookedAppointmentSummary && (showPaymentForm || showPaymentReceipt) && (
          <>
            {renderAppointmentSummary()}
            {renderCollectCopay()}
          </>
        )}

      {showPayConfirmation && !payConfirmationShown && (
        <GenericConfirmationModal
          isOpen={showPayConfirmation}
          onCancel={handleRefuseCopay}
          onConfirm={handleConfirmCopay}
          title={"Co-Pay Payment Required"}
          textCancel="Cancel"
          textConfirm="Continue"
        >
          <div className="text-gray-600 text-center text-wrap">
            {dialogMessage}
          </div>
          <div className="text-sm text-gray-400 text-left py-2">
            To schedule your next appointment, a <strong>${user?.copay_fee}</strong>  co-payment will be collected during booking. Let's get that set up for you.
          </div>
          <div className="text-gray-600 text-center my-2" >
            Would you like to continue?
          </div>
        </GenericConfirmationModal>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && selectedDoctor && selectedTimeSlot && (
        <BookingConfirmationModal
          isOpen={showConfirmation}
          onClose={handleCancelBooking}
          onConfirm={handleConfirmBooking}
          doctorName={selectedDoctor.name}
          appointmentTime={selectedTimeSlot} // Pass the full ISO UTC string
          isLoading={isBooking}
          onToggleSendMessage={setSendMessage}
          isSendMessage={sendMessage}
        />
      )}
    </>
  );
}

export default SchedulePage;
